import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Conversion between wall-clock time (the reading on a clock in an IANA
// timezone) and instants (unix seconds).
//
// This ensures stable stored timestamps and consistently displayed times
// across timezones:
//
// ┌──────────────────┬──────────────────────┬────────────────┐
// │       Zone       │ Wall Clock           │ Unix Timestamp │
// ├──────────────────┼──────────────────────┼────────────────┤
// │ UTC              │ 2026-09-02 08:00:00  │ same instant   │
// ├──────────────────┼──────────────────────┼────────────────┤
// │ Europe/Stockholm │ 2026-09-02 10:00:00  │ same instant   │
// ├──────────────────┼──────────────────────┼────────────────┤
// │ Pacific/Auckland │ 2026-09-02 20:00:00  │ same instant   │
// └──────────────────┴──────────────────────┴────────────────┘
//
// These two primitives are the only place in the app that should resolve a
// timezone offset. They work identically in any browser timezone, which the
// dayjs instance methods do not:
//
//   dayjs(x).tz(zone, true)          keepLocalTime, documented at https://day.js.org/docs/en/timezone/converting-to-zone
//   dayjs(x).tz(zone).format(...)    display conversion
//   dayjs(x).tz(zone).startOf(...)   and add/subtract on a tz-tagged instance
//
// all derive the offset by round-tripping through the browser's own timezone,
// and are off by an hour on daylight-saving switch days in either the browser
// or the target zone. See https://github.com/iamkun/dayjs/issues/2162 and
// https://github.com/iamkun/dayjs/issues/3123, and the tests in
// tests/timeConverter.test.ts.

dayjs.extend(utc);
dayjs.extend(timezone);

export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/**
 * How a wall-clock value is represented. 'startOfDay' truncates to the
 * calendar date, i.e. midnight, as a 'YYYY-MM-DD' string. 'dateTime' keeps the
 * time, as a 'YYYY-MM-DD HH:mm:ss' string. 'dayjs' is a Dayjs carrying the
 * full digits.
 */
export type Mode = 'startOfDay' | 'dateTime' | 'dayjs';
type StringMode = Exclude<Mode, 'dayjs'>;

const FORMATS: Record<StringMode, string> = {
  startOfDay: DATE_FORMAT,
  dateTime: DATE_TIME_FORMAT,
};

/**
 * A wall-clock value: the reading on a clock, without a timezone. A Dayjs
 * contributes only its digits. Whatever timezone the instance itself carries
 * is ignored.
 */
export type WallClock = string | Dayjs;

const wallClockString = (value: WallClock, mode: StringMode) =>
  dayjs.isDayjs(value) ? value.format(FORMATS[mode]) : value;

/**
 * The instant at which a wall-clock time occurs in `timezone`.
 *
 * `dayjs.tz(string, zone)` searches for the offset that applies at that wall
 * clock, so it is exact around daylight-saving switches.
 *
 * @param wallClock - a string, or a Dayjs whose digits are read
 * @param timezone - IANA identifier, e.g. 'Europe/Stockholm'
 * @param mode - which digits of a Dayjs to read; 'startOfDay' means midnight
 * of its calendar date
 * @returns unix seconds
 * @throws RangeError for an unknown or empty timezone
 */
export const wallClockToUnix = (
  wallClock: WallClock,
  timezone: string,
  mode: StringMode = 'dateTime'
): number => {
  if (!timezone) {
    // dayjs.tz treats an empty zone as "use the default", which would silently
    // fall back to the browser timezone.
    throw new RangeError(`Invalid time zone specified: ${timezone}`);
  }
  return dayjs.tz(wallClockString(wallClock, mode), timezone).unix();
};

const formatters = new Map<string, Intl.DateTimeFormat>();

const formatterFor = (timezone: string, mode: StringMode) => {
  const key = `${timezone}|${mode}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    // sv-SE yields 'YYYY-MM-DD' and 'YYYY-MM-DD HH:mm:ss' directly.
    formatter = new Intl.DateTimeFormat('sv-SE', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(mode === 'dateTime' && {
        hourCycle: 'h23',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    });
    formatters.set(key, formatter);
  }
  return formatter;
};

/**
 * The wall-clock time of an instant in `timezone`.
 *
 * @param unix - unix seconds
 * @param timezone - IANA identifier
 * @param mode - 'startOfDay' for a 'YYYY-MM-DD' string, 'dateTime' for a
 * 'YYYY-MM-DD HH:mm:ss' string, 'dayjs' for a Dayjs carrying the full digits,
 * e.g. as a picker value
 * @throws RangeError for an unknown or empty timezone
 */
export function unixToWallClock(
  unix: number,
  timezone: string,
  mode?: StringMode
): string;
export function unixToWallClock(
  unix: number,
  timezone: string,
  mode: 'dayjs'
): Dayjs;
export function unixToWallClock(
  unix: number,
  timezone: string,
  mode: Mode = 'dateTime'
): WallClock {
  const date = new Date(unix * 1000);
  return mode === 'dayjs'
    ? dayjs(formatterFor(timezone, 'dateTime').format(date))
    : formatterFor(timezone, mode).format(date);
}

/**
 * Move a calendar date by whole days. Done in UTC so the arithmetic never
 * crosses a daylight-saving switch. Returns the same kind of value it was
 * given.
 *
 * @param wallClock - a string, or a Dayjs whose digits are read
 * @param days - The number of days to shift the date by
 * @returns A string or a Dayjs
 */
export function shiftDays(wallClock: string, days: number): string;
export function shiftDays(wallClock: Dayjs, days: number): Dayjs;
export function shiftDays(wallClock: WallClock, days: number): WallClock {
  const shifted = dayjs
    .utc(wallClockString(wallClock, 'startOfDay'))
    .add(days, 'day')
    .format(DATE_FORMAT);
  return dayjs.isDayjs(wallClock) ? dayjs(shifted) : shifted;
}

/**
 * Whether the runtime recognizes `timezone` as an IANA identifier. The
 * converters above throw for anything else.
 */
export const isKnownTimeZone = (timezone: string | undefined): boolean => {
  if (!timezone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

/**
 * Chooses which of a time zone's IANA identifiers to use for conversion. The
 * identifiers are aliases sharing the same rules, but not every alias is known
 * to every runtime, so the first one the runtime recognizes is returned.
 *
 * @returns an identifier, or `undefined` when none is usable
 */
export const pickTimeZoneIdentifier = (
  identifiers: readonly string[] | undefined
): string | undefined => identifiers?.find(isKnownTimeZone);
